/**
 * Quiet-timeline activity filtering and work-log orchestration (ticket 3525,
 * upstream pin e3e7cc3f, MIT), ported from upstream
 * `apps/web/src/session-logic.ts` around forge's turn-scope gate.
 *
 * Forge deltas kept on purpose: the work log shows the active turn (plus
 * bootstrap and turnless failures) rather than the whole thread, the debug
 * disclosure gate keeps no-content runtime warnings inspectable, and agent
 * task.started rows bypass forge's blanket task.started hide because they seed
 * spawn CTA batches.
 *
 * @module workLogActivityFiltering
 */

import type { OrchestrationThreadActivity, TurnId } from "@cmmd-forge/contracts";
import { isHiddenWorkLogActivity } from "./usage/workLogHiddenActivities";
import { isNoContentRuntimeWarning } from "./workLogDetail";
import { collapseDerivedWorkLogEntries } from "./workLogEntryCollapse";
import {
  toDerivedWorkLogEntry,
  type DerivedWorkLogEntry,
  type WorkLogEntry,
} from "./workLogEntryDerivation";
import { asRecord, asTrimmedString } from "./workLogEntryExtractors";
import { isInWorkLogTurnScope } from "./workLogTurnScope";
import { foldUserInputActivities } from "./workLogUserInput";

/** Upstream stamps agent tasks with agentKind "agent"; unstamped rows are background. */
function isAgentKindPayload(payload: Record<string, unknown>): boolean {
  return payload.agentKind === "agent";
}

/** Agent (non-background) task.started rows seed spawn CTA batches. */
function isAgentTaskStartedActivity(activity: OrchestrationThreadActivity): boolean {
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  if (!payload || typeof payload.taskId !== "string") {
    return false;
  }
  return isAgentKindPayload(payload);
}

// fallow-ignore-next-line complexity -- port of upstream session-logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function isAgentInternalActivity(activity: OrchestrationThreadActivity): boolean {
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  if (!payload) {
    return false;
  }
  const isTaskRow =
    activity.kind === "task.started" ||
    activity.kind === "task.progress" ||
    activity.kind === "task.updated" ||
    activity.kind === "task.completed";
  // Task rows classify by the server stamp: a subagent's own background
  // shell (agentId + background) is agent-internal, but a nested AGENT
  // (agentId + agent) stays visible so its rows can anchor a spawn row
  // (review finding: hiding on agentId alone removed nested agents and
  // their anchors). Bypassed agent lifecycle rows also pass — collapse
  // folds every such row into its batch's single CTA row, which is how
  // Codex children (whose rows are ALL bypassed) get an anchor at the
  // spawn point.
  if (isTaskRow) {
    const ownedByAgent = typeof payload.agentId === "string" && payload.agentId.trim().length > 0;
    if (ownedByAgent || payload.timelineBypass === true) {
      const isAgentTaskRow =
        activity.kind !== "task.updated" &&
        typeof payload.taskId === "string" &&
        isAgentKindPayload(payload);
      return !isAgentTaskRow;
    }
    return false;
  }
  if (payload.timelineBypass === true) {
    return true;
  }
  // Non-task rows (attributed tool activity) owned by an agent are internal.
  return typeof payload.agentId === "string" && payload.agentId.trim().length > 0;
}

function isDebugDisclosureEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("forge:debug-mode") === "true";
  } catch {
    return false;
  }
}

function isPlanBoundaryToolActivity(activity: OrchestrationThreadActivity): boolean {
  if (activity.kind !== "tool.updated" && activity.kind !== "tool.completed") {
    return false;
  }

  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  return typeof payload?.detail === "string" && payload.detail.startsWith("ExitPlanMode:");
}

// fallow-ignore-next-line complexity -- port of upstream session-logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function deriveWorkLogEntries(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurnId: TurnId | undefined,
  latestTurnStartedAt?: string | null,
): WorkLogEntry[] {
  // Forge's debug gate (ticket 3522) keeps even no-content runtime warning
  // rows visible in debug builds, the same disclosure path as provider
  // provenance. Upstream drops them unconditionally.
  const showNoContentRuntimeWarnings = isDebugDisclosureEnabled();
  const turnScope = { latestTurnId, latestTurnStartedAt: latestTurnStartedAt ?? null };
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  // A launch tool and its task lifecycle describe the same run. Only hide
  // launch rows once their tool-use id has an agent row to replace them.
  const agentLaunchToolIds = new Set<string>();
  for (const activity of ordered) {
    if (
      (activity.kind === "task.started" ||
        activity.kind === "task.progress" ||
        activity.kind === "task.completed") &&
      isAgentTaskStartedActivity(activity)
    ) {
      const toolUseId = asTrimmedString(asRecord(activity.payload)?.toolUseId);
      if (toolUseId) agentLaunchToolIds.add(toolUseId);
    }
  }
  const entries: DerivedWorkLogEntry[] = [];
  for (const activity of foldUserInputActivities(ordered)) {
    // Forge scope: the work log shows the active turn (plus bootstrap and
    // turnless failures), not the whole thread.
    if (!isInWorkLogTurnScope(activity, turnScope)) continue;
    // Agent task.started rows seed spawn CTAs, so they bypass forge's blanket
    // task.started hide; every other hidden kind stays out.
    const seedsSpawnCta = activity.kind === "task.started" && isAgentTaskStartedActivity(activity);
    if (!seedsSpawnCta && isHiddenWorkLogActivity(activity)) continue;
    if (activity.kind === "task.started" && !isAgentTaskStartedActivity(activity)) continue;
    if (activity.kind === "task.updated") continue;
    if (activity.kind === "tool.progress") continue;
    if (activity.kind === "turn.plan.updated") continue;
    if (!showNoContentRuntimeWarnings && isNoContentRuntimeWarning(activity)) continue;
    if (isPlanBoundaryToolActivity(activity)) continue;
    if (isAgentInternalActivity(activity)) continue;
    const entry = toDerivedWorkLogEntry(activity);
    // Native agent launches get their visible row from task.started. Defer
    // their active tool row so another launch cannot duplicate the batch.
    if (
      activity.kind === "tool.updated" &&
      entry.itemType === "collab_agent_tool_call" &&
      entry.toolLifecycleStatus === "inProgress" &&
      entry.tone !== "error"
    ) {
      const toolName = asRecord(asRecord(activity.payload)?.data)?.toolName;
      if (toolName === "Agent" || toolName === "Task") continue;
    }
    if (
      (activity.kind === "tool.updated" || activity.kind === "tool.completed") &&
      entry.toolCallId &&
      agentLaunchToolIds.has(entry.toolCallId) &&
      entry.tone !== "error" &&
      entry.toolLifecycleStatus !== "failed"
    ) {
      continue;
    }
    entries.push(entry);
  }
  return collapseDerivedWorkLogEntries(entries).map(
    ({
      sourceActivityKind: _sourceActivityKind,
      collapseKey: _collapseKey,
      isWorkflowCoordinator: _isWorkflowCoordinator,
      isBackgroundTask: _isBackgroundTask,
      ...entry
    }) => entry,
  );
}

export function compareActivitiesByOrder(
  left: OrchestrationThreadActivity,
  right: OrchestrationThreadActivity,
): number {
  if (left.sequence !== undefined && right.sequence !== undefined) {
    if (left.sequence !== right.sequence) {
      return left.sequence - right.sequence;
    }
  } else if (left.sequence !== undefined) {
    return 1;
  } else if (right.sequence !== undefined) {
    return -1;
  }

  const createdAtComparison = left.createdAt.localeCompare(right.createdAt);
  if (createdAtComparison !== 0) {
    return createdAtComparison;
  }

  const lifecycleRankComparison =
    compareActivityLifecycleRank(left.kind) - compareActivityLifecycleRank(right.kind);
  if (lifecycleRankComparison !== 0) {
    return lifecycleRankComparison;
  }

  return left.id.localeCompare(right.id);
}

function compareActivityLifecycleRank(kind: string): number {
  if (kind.endsWith(".started") || kind === "tool.started") {
    return 0;
  }
  if (kind.endsWith(".progress") || kind.endsWith(".updated")) {
    return 1;
  }
  if (kind.endsWith(".completed") || kind.endsWith(".resolved")) {
    return 2;
  }
  return 1;
}
